"use client";

import * as React from "react";
import {
  CONSENT_STORAGE_KEY,
  type ConsentCategory,
  type ConsentContextValue,
  type ConsentState,
  DEFAULT_CONSENT_STATE,
} from "./types";

/**
 * Cookie Consent Context
 *
 * Provides consent state and methods for managing user cookie preferences.
 * Persists to localStorage for GDPR compliance.
 */

const ConsentContext = React.createContext<ConsentContextValue | undefined>(undefined);

/**
 * Hook to use the consent context. Must be used within a ConsentProvider.
 */
export function useConsent() {
  const context = React.useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return context;
}

/**
 * Hook to check if a specific consent category is allowed.
 * Safe to use outside ConsentProvider (returns false if no provider).
 */
export function useConsentCategory(category: ConsentCategory): boolean {
  try {
    const context = React.useContext(ConsentContext);
    if (!context) return false;
    return context.isConsented(category);
  } catch {
    return false;
  }
}

interface ConsentProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component for cookie consent management.
 * Loads persisted consent from localStorage on mount.
 */
export function ConsentProvider({ children }: ConsentProviderProps) {
  const [consent, setConsent] = React.useState<ConsentState>(DEFAULT_CONSENT_STATE);
  const [isLoaded, setIsLoaded] = React.useState(false);

  // Load from localStorage on mount (client-side only)
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ConsentState;
        // Validate and merge with defaults (in case schema changed)
        setConsent({
          ...DEFAULT_CONSENT_STATE,
          ...parsed,
          categories: {
            ...DEFAULT_CONSENT_STATE.categories,
            ...parsed.categories,
            necessary: true, // Always ensure necessary is true
          },
        });
      }
    } catch {
      // Invalid stored data, use defaults
    }
    setIsLoaded(true);
  }, []);

  // Persist to localStorage whenever consent changes
  React.useEffect(() => {
    if (!isLoaded) return;
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    } catch {
      // Storage not available
    }
  }, [consent, isLoaded]);

  const acceptAll = React.useCallback(() => {
    setConsent({
      version: DEFAULT_CONSENT_STATE.version,
      timestamp: Date.now(),
      hasConsented: true,
      categories: {
        necessary: true,
        analytics: true,
        marketing: true,
      },
    });
  }, []);

  const rejectNonEssential = React.useCallback(() => {
    setConsent({
      version: DEFAULT_CONSENT_STATE.version,
      timestamp: Date.now(),
      hasConsented: true,
      categories: {
        necessary: true,
        analytics: false,
        marketing: false,
      },
    });
  }, []);

  const setCategory = React.useCallback((category: ConsentCategory, value: boolean) => {
    if (category === "necessary") return; // Cannot disable necessary
    setConsent((prev) => ({
      ...prev,
      timestamp: Date.now(),
      hasConsented: true,
      categories: {
        ...prev.categories,
        [category]: value,
      },
    }));
  }, []);

  const setCategories = React.useCallback(
    (categories: Partial<Record<ConsentCategory, boolean>>) => {
      setConsent((prev) => ({
        ...prev,
        timestamp: Date.now(),
        hasConsented: true,
        categories: {
          ...prev.categories,
          ...categories,
          necessary: true, // Always keep necessary
        },
      }));
    },
    [],
  );

  const reset = React.useCallback(() => {
    setConsent(DEFAULT_CONSENT_STATE);
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch {
      // Storage not available
    }
  }, []);

  const isConsented = React.useCallback(
    (category: ConsentCategory) => {
      return consent.categories[category] ?? false;
    },
    [consent.categories],
  );

  const value = React.useMemo(
    () => ({
      consent,
      isLoaded,
      acceptAll,
      rejectNonEssential,
      setCategory,
      setCategories,
      reset,
      isConsented,
    }),
    [
      consent,
      isLoaded,
      acceptAll,
      rejectNonEssential,
      setCategory,
      setCategories,
      reset,
      isConsented,
    ],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}
